import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { Check, CheckCheck, Trash2, Calendar, Briefcase, CheckSquare, Megaphone, Clock, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";

interface Notification {
  id: number;
  userId: number;
  type: string;
  title: string;
  message: string;
  link: string | null;
  isRead: boolean;
  metadata: any;
  createdAt: string;
}

function getCategoryIcon(type: string) {
  if (type.startsWith("leave_")) return <Calendar className="h-4 w-4 text-blue-500" />;
  if (type.startsWith("wfh_")) return <MapPin className="h-4 w-4 text-green-500" />;
  if (type.startsWith("task_")) return <CheckSquare className="h-4 w-4 text-purple-500" />;
  if (type.startsWith("effort_")) return <Clock className="h-4 w-4 text-orange-500" />;
  if (type.startsWith("system_")) return <Megaphone className="h-4 w-4 text-red-500" />;
  return <Briefcase className="h-4 w-4 text-muted-foreground" />;
}

function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}

function isToday(dateStr: string): boolean {
  const date = new Date(dateStr);
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}

interface NotificationDropdownProps {
  onClose: () => void;
}

export default function NotificationDropdown({ onClose }: NotificationDropdownProps) {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();

  const { data: notificationsData, isLoading } = useQuery<Notification[]>({
    queryKey: ["/api/notifications?limit=20"],
  });

  const markReadMutation = useMutation({
    mutationFn: (id: number) =>
      apiRequest("PATCH", `/api/notifications/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => apiRequest("PATCH", "/api/notifications/read-all"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      apiRequest("DELETE", `/api/notifications/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
    },
  });

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      markReadMutation.mutate(notification.id);
    }
    if (notification.link) {
      onClose();
      setLocation(notification.link);
    }
  };

  const allNotifications = notificationsData || [];
  const todayNotifications = allNotifications.filter((n) => isToday(n.createdAt));
  const earlierNotifications = allNotifications.filter((n) => !isToday(n.createdAt));
  const hasUnread = allNotifications.some((n) => !n.isRead);

  return (
    <div className="flex flex-col max-h-[480px]">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="font-semibold text-sm">Notifications</h3>
        {hasUnread && (
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-7"
            onClick={() => markAllReadMutation.mutate()}
            disabled={markAllReadMutation.isPending}
          >
            <CheckCheck className="h-3.5 w-3.5 mr-1" />
            Mark all as read
          </Button>
        )}
      </div>

      <ScrollArea className="flex-1 min-h-0">
        {isLoading ? (
          <div className="space-y-1 p-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-start gap-3 px-2 py-3">
                <Skeleton className="h-4 w-4 rounded-full flex-shrink-0 mt-0.5" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-16" />
                </div>
              </div>
            ))}
          </div>
        ) : allNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Check className="h-8 w-8 mb-2 text-green-500" />
            <p className="text-sm font-medium">You're all caught up!</p>
            <p className="text-xs text-muted-foreground mt-1">No new notifications</p>
          </div>
        ) : (
          <div>
            {todayNotifications.length > 0 && (
              <div>
                <div className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider bg-muted">
                  Today
                </div>
                {todayNotifications.map((notification) => (
                  <NotificationRow
                    key={notification.id}
                    notification={notification}
                    onClick={handleNotificationClick}
                    onDelete={(id) => deleteMutation.mutate(id)}
                  />
                ))}
              </div>
            )}

            {earlierNotifications.length > 0 && (
              <div>
                <div className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider bg-muted">
                  Earlier
                </div>
                {earlierNotifications.map((notification) => (
                  <NotificationRow
                    key={notification.id}
                    notification={notification}
                    onClick={handleNotificationClick}
                    onDelete={(id) => deleteMutation.mutate(id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </ScrollArea>

      {!isLoading && allNotifications.length > 0 && (
        <div className="border-t px-4 py-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-xs text-muted-foreground hover:text-foreground"
            onClick={() => {
              onClose();
              setLocation("/notifications");
            }}
          >
            View all notifications
          </Button>
        </div>
      )}
    </div>
  );
}

function NotificationRow({
  notification,
  onClick,
  onDelete,
}: {
  notification: Notification;
  onClick: (n: Notification) => void;
  onDelete: (id: number) => void;
}) {
  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-muted border-b border-border last:border-0 transition-colors ${
        !notification.isRead ? "bg-blue-50/50 dark:bg-blue-950/30" : ""
      }`}
      onClick={() => onClick(notification)}
    >
      <div className="mt-0.5 flex-shrink-0">
        {getCategoryIcon(notification.type)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm leading-snug ${!notification.isRead ? "font-semibold text-foreground" : "text-foreground"}`}>
            {notification.title}
          </p>
          {!notification.isRead && (
            <span className="flex-shrink-0 mt-1.5 h-2 w-2 rounded-full bg-blue-500" />
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{notification.message}</p>
        <p className="text-xs text-muted-foreground mt-1">{timeAgo(notification.createdAt)}</p>
      </div>
      <button
        className="flex-shrink-0 mt-0.5 p-1 rounded hover:bg-accent text-muted-foreground hover:text-red-500 transition-colors"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(notification.id);
        }}
        title="Delete notification"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
